import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Send,
  Plus,
  Search,
  Lock,
  User,
  MessageSquare,
  Loader2,
  Palette,
  FileCode,
  Server,
  Beaker,
  CheckCircle2,
  TerminalSquare,
  Clock3,
  LayoutDashboard,
  ListChecks,
  Layers3,
  PanelLeft,
} from "lucide-react";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import axiosPublic from "../../api/axios";

// Projects will be loaded from backend

const TABS = [
  { key: "ui", label: "UI", icon: Palette, enabled: true },
  { key: "frontend", label: "Frontend", icon: FileCode, enabled: false },
  { key: "backend", label: "Backend", icon: Server, enabled: false },
  { key: "testing", label: "Testing", icon: Beaker, enabled: false },
];

const STATUS_META = {
  idle: {
    label: "Idle",
    tone: "text-gray-600 dark:text-light-400",
    dot: "bg-gray-400",
    icon: MessageSquare,
  },
  thinking: {
    label: "Thinking",
    tone: "text-blue-700 dark:text-blue-300",
    dot: "bg-blue-500",
    icon: Loader2,
  },
  planning: {
    label: "Planning",
    tone: "text-sky-700 dark:text-sky-300",
    dot: "bg-sky-500",
    icon: Loader2,
  },
  generating_ui: {
    label: "Generating UI",
    tone: "text-primary-accent",
    dot: "bg-primary-accent",
    icon: Sparkles,
  },
  completed: {
    label: "Completed",
    tone: "text-green-700 dark:text-green-300",
    dot: "bg-green-500",
    icon: CheckCircle2,
  },
};

const WORKFLOW_STAGES = [
  {
    key: "reading",
    label: "Reading project context",
    keywords: ["Reading project context", "Reading project components"],
    icon: PanelLeft,
  },
  {
    key: "analyzing",
    label: "Analyzing task intent",
    keywords: ["Analyzing user task", "Thinking:", "Analyzing task intent"],
    icon: MessageSquare,
  },
  {
    key: "components",
    label: "Selecting reusable components",
    keywords: ["Selecting reusable components"],
    icon: Layers3,
  },
  {
    key: "layout",
    label: "Building responsive layout",
    keywords: ["Building layout structure", "Generating layout"],
    icon: LayoutDashboard,
  },
  {
    key: "plan",
    label: "Generating execution plan",
    keywords: ["Plan generated"],
    icon: ListChecks,
  },
  {
    key: "finalizing",
    label: "Finalizing UI structure",
    keywords: ["Finalizing UI plan", "Completed at"],
    icon: CheckCircle2,
  },
];

const formatTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (start, end) => {
  if (!start || !end) return "-";
  const seconds = Math.max(
    1,
    Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000),
  );
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

const getComponentDisplayName = (component) => {
  if (!component) return "";
  if (typeof component === "string") return component;
  if (typeof component === "object") {
    return component.name || component.label || component.id || "";
  }
  return String(component);
};

const summarizePlan = (plan, projectName) => {
  if (!plan) return "";
  const sectionCount = Array.isArray(plan.sections) ? plan.sections.length : 0;
  const components = Array.isArray(plan.sections)
    ? plan.sections
        .map((section) => getComponentDisplayName(section.component))
        .filter(Boolean)
        .slice(0, 4)
    : [];
  const notes = Array.isArray(plan.notes) ? plan.notes : [];
  const reason =
    notes[0] ||
    `The layout is tailored to ${projectName || "the selected project"} and reuses available components.`;
  return {
    reason,
    sectionCount,
    components,
  };
};

const getLatestPlan = (workflow) => {
  if (!workflow) return null;
  if (workflow.result && typeof workflow.result === "object")
    return workflow.result;
  const planMessage = [...(workflow.messages || [])]
    .reverse()
    .find((entry) => entry.type === "plan");
  return planMessage?.content || null;
};

const getTimelineProgressIndex = (workflow, logs = []) => {
  if (!workflow) return 0;
  if (workflow.status === "completed") return WORKFLOW_STAGES.length - 1;
  if (workflow.status === "failed")
    return Math.min(1, WORKFLOW_STAGES.length - 1);

  const joined = logs.join(" | ");
  let latestIndex = 0;
  WORKFLOW_STAGES.forEach((stage, index) => {
    if (stage.key === "reading" && workflow.status)
      latestIndex = Math.max(latestIndex, 0);
    if (
      stage.key === "analyzing" &&
      /Thinking:|Analyzing user task/.test(joined)
    )
      latestIndex = Math.max(latestIndex, index);
    if (
      stage.key === "components" &&
      /Selecting reusable components/.test(joined)
    )
      latestIndex = Math.max(latestIndex, index);
    if (
      stage.key === "layout" &&
      /Building layout structure|Generating layout/.test(joined)
    )
      latestIndex = Math.max(latestIndex, index);
    if (stage.key === "plan" && /Plan generated/.test(joined))
      latestIndex = Math.max(latestIndex, index);
    if (
      stage.key === "finalizing" &&
      /Finalizing UI plan|Completed at/.test(joined)
    )
      latestIndex = Math.max(latestIndex, index);
  });

  if (workflow.status === "thinking") return Math.max(latestIndex, 1);
  if (workflow.status === "planning") return Math.max(latestIndex, 3);
  if (workflow.status === "generating_ui") return Math.max(latestIndex, 4);
  return latestIndex;
};

const getTimelineStageState = (index, activeIndex, workflow) => {
  if (workflow?.status === "failed" && index >= activeIndex) return "failed";
  if (index < activeIndex) return "done";
  if (index === activeIndex) return "active";
  return "pending";
};

const AIStudio = () => {
  const [workflows, setWorkflows] = useState([]);
  const [activeWorkflowId, setActiveWorkflowId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [message, setMessage] = useState("");

  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const timersRef = useRef([]);

  const activeWorkflow = useMemo(
    () => workflows.find((w) => (w._id || w.id) === activeWorkflowId) || null,
    [workflows, activeWorkflowId],
  );

  const selectedProject = useMemo(() => {
    if (!activeWorkflow?.projectKey) return null;
    return projects.find((p) => p.key === activeWorkflow.projectKey) || null;
  }, [activeWorkflow?.projectKey, projects]);

  const activeMessages = activeWorkflow?.messages || [];
  const activeLogs = activeWorkflow?.logs || [];
  const activePlan = getLatestPlan(activeWorkflow);
  const timelineProgressIndex = getTimelineProgressIndex(
    activeWorkflow,
    activeLogs,
  );
  const planSummary = summarizePlan(activePlan, selectedProject?.name);
  const workflowDuration = activeWorkflow
    ? formatDuration(activeWorkflow.createdAt, activeWorkflow.updatedAt)
    : "-";

  const filteredWorkflows = useMemo(() => {
    const keyword = historySearch.trim().toLowerCase();
    if (!keyword) return workflows;
    return workflows.filter((wf) => {
      const projectLabel =
        projects.find((p) => p.key === wf.projectKey)?.name || "";
      return (
        wf.title.toLowerCase().includes(keyword) ||
        projectLabel.toLowerCase().includes(keyword)
      );
    });
  }, [historySearch, workflows, projects]);

  // Polling interval ref
  const pollRef = useRef(null);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 220)}px`;
  }, [message]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [activeWorkflow?.messages, activeWorkflow?.status]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  const updateWorkflowById = (id, updater) => {
    setWorkflows((prev) =>
      prev.map((wf) =>
        (wf._id || wf.id) !== id
          ? wf
          : { ...updater(wf), updatedAt: new Date() },
      ),
    );
  };

  const handleNewWorkflow = () => {
    // create workflow on backend
    (async () => {
      try {
        const resp = await axiosPublic.post(`/api/ai-studio/workflows`, {
          title: "Untitled Workflow",
        });
        const wf = resp.data;
        // convert _id to id if present
        const item = { ...wf, id: wf._id || wf.id };
        setWorkflows((prev) => [item, ...prev]);
        setActiveWorkflowId(item._id || item.id);
        setMessage("");
      } catch (err) {
        console.error("Failed to create workflow", err);
      }
    })();
  };

  const handleSelectProject = (projectKey) => {
    if (!activeWorkflow) return;
    // update backend workflow
    (async () => {
      try {
        const id = activeWorkflow._id || activeWorkflow.id;
        const resp = await axiosPublic.patch(`/api/ai-studio/workflows/${id}`, {
          projectKey,
        });
        const updated = resp.data;
        setWorkflows((prev) =>
          prev.map((w) =>
            (w._id || w.id) === (updated._id || updated.id)
              ? { ...w, ...updated, id: updated._id || updated.id }
              : w,
          ),
        );
      } catch (err) {
        console.error("Failed to update workflow project", err);
      }
    })();
  };

  const appendMessage = (workflowId, payload) => {
    updateWorkflowById(workflowId, (wf) => ({
      ...wf,
      messages: [
        ...wf.messages,
        {
          id: `${payload.role}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          timestamp: payload.timestamp || new Date(),
          ...payload,
        },
      ],
    }));
  };

  const handleSend = () => {
    const content = message.trim();
    if (!content || !activeWorkflow) return;
    clearTimers();

    const wfId = activeWorkflow._id || activeWorkflow.id;

    // Optimistically append user message
    appendMessage(wfId, {
      role: "user",
      type: "text",
      content,
      timestamp: new Date(),
    });

    // Post message to backend which triggers orchestration
    (async () => {
      try {
        await axiosPublic.post(`/api/ai-studio/workflows/${wfId}/messages`, {
          role: "user",
          content,
          type: "text",
        });
        setMessage("");

        // start polling workflow updates
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          try {
            const resp = await axiosPublic.get(
              `/api/ai-studio/workflows/${wfId}`,
            );
            const updated = resp.data;
            // normalize id
            updated.id = updated._id || updated.id;
            setWorkflows((prev) => {
              const found = prev.some(
                (w) => (w._id || w.id) === (updated._id || updated.id),
              );
              if (!found) return [updated, ...prev];
              return prev.map((w) =>
                (w._id || w.id) === (updated._id || updated.id) ? updated : w,
              );
            });

            // If workflow has assistant messages, ensure they are rendered (server will include messages)
            if (updated.status === "completed" || updated.status === "failed") {
              if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
              }
            }
          } catch (err) {
            console.error("Polling error", err);
          }
        }, 2000);
      } catch (err) {
        console.error("Failed to post message", err);
      }
    })();
  };

  const onComposerKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const currentStatusMeta =
    STATUS_META[activeWorkflow?.status || "idle"] || STATUS_META.idle;
  const StatusIcon = currentStatusMeta.icon;

  // Load projects and workflows on mount
  useEffect(() => {
    (async () => {
      try {
        const [projResp, wfResp] = await Promise.all([
          axiosPublic.get(`/api/ai-studio/projects`),
          axiosPublic.get(`/api/ai-studio/workflows`),
        ]);
        const projs = projResp.data || [];
        const wfs = (wfResp.data || []).map((w) => ({
          ...w,
          id: w._id || w.id,
        }));
        setProjects(projs);
        setWorkflows(wfs);
        setActiveWorkflowId(
          (current) => current || wfs[0]?.id || wfs[0]?._id || null,
        );
      } catch (err) {
        console.error("Failed to load AI Studio data", err);
      }
    })();
  }, []);
  return (
    <PageWrapper
      title="AI Studio"
      subtitle="AI-powered workspace for UI generation and development workflows."
    >
      <div className="grid grid-cols-1 xl:grid-cols-[310px_minmax(0,1fr)] gap-4 sm:gap-5">
        {/* Left: History */}
        <aside className="bg-white dark:bg-primary-card border border-gray-200 dark:border-dark-100 rounded-2xl shadow-sm p-4 flex flex-col min-h-[560px] h-[calc(100vh-14.5rem)]">
          <Button
            onClick={handleNewWorkflow}
            className="w-full"
            icon={<Plus className="w-4 h-4" />}
          >
            New Workflow
          </Button>

          <div className="relative mt-3 mb-3">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              placeholder="Search conversations"
              className="input-field w-full pl-9 pr-3 py-2.5 text-sm"
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {filteredWorkflows.length === 0 ? (
              <div className="h-full min-h-[220px] rounded-xl border border-dashed border-gray-300 dark:border-dark-100 bg-gray-50/70 dark:bg-dark-300/30 flex items-center justify-center text-center px-4">
                <p className="text-sm text-gray-600 dark:text-light-500">
                  No workflow history yet. Create a new workflow to start.
                </p>
              </div>
            ) : (
              filteredWorkflows.map((wf) => {
                const isActive = (wf._id || wf.id) === activeWorkflowId;
                const statusMeta =
                  STATUS_META[wf.status || "idle"] || STATUS_META.idle;
                const projectLabel =
                  projects.find((p) => p.key === wf.projectKey)?.name ||
                  "No project selected";
                return (
                  <button
                    key={wf._id || wf.id}
                    onClick={() => setActiveWorkflowId(wf._id || wf.id)}
                    className={`w-full text-left rounded-xl border p-3 transition-all ${isActive ? "border-primary-accent bg-primary-accent/10 dark:bg-primary-accent/15 shadow-md shadow-primary-accent/15" : "border-gray-200 dark:border-dark-100 bg-gray-50/60 dark:bg-dark-300/30 hover:border-primary-accent/40"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900 dark:text-light-100 line-clamp-2">
                        {wf.title}
                      </p>
                      <span
                        className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${statusMeta.dot}`}
                      />
                    </div>
                    <p className="text-xs text-gray-600 dark:text-light-500 mt-1">
                      {projectLabel}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-light-500 mt-2">
                      Last activity: {formatTime(wf.updatedAt)}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Right: Workspace */}
        <div className="bg-white dark:bg-primary-card border border-gray-200 dark:border-dark-100 rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-[560px] h-[calc(100vh-14.5rem)]">
          {workflows.length === 0 ? (
            <div className="h-full flex items-center justify-center p-6">
              <div className="w-full max-w-xl text-center rounded-2xl border border-dashed border-gray-300 dark:border-dark-100 bg-gray-50/80 dark:bg-dark-300/30 p-10">
                <div className="w-14 h-14 mx-auto rounded-xl bg-primary-accent/10 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-primary-accent" />
                </div>
                <p className="text-xl font-semibold text-gray-900 dark:text-light-100">
                  Build your first AI workflow
                </p>
                <p className="text-sm text-gray-600 dark:text-light-500 mt-2 mb-5">
                  Create a workflow session to manage separate UI generations
                  for different projects and tasks.
                </p>
                <Button
                  onClick={handleNewWorkflow}
                  icon={<Plus className="w-4 h-4" />}
                >
                  New Workflow
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="px-4 sm:px-5 pt-4 sm:pt-5 border-b border-gray-200 dark:border-dark-100 bg-gray-50/70 dark:bg-dark-300/25">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3">
                  {projects.map((item) => {
                    const isActive = item.key === selectedProject?.key;
                    return (
                      <button
                        key={item.key}
                        onClick={() => handleSelectProject(item.key)}
                        className={`relative text-left rounded-xl border p-3.5 transition-all duration-300 overflow-hidden transform-gpu ${isActive ? "border-primary-accent bg-primary-accent/8 dark:bg-primary-accent/15 shadow-lg shadow-primary-accent/20 -translate-y-0.5" : "border-gray-200 dark:border-dark-100 bg-gray-50/70 dark:bg-dark-300/40 hover:border-primary-accent/45 hover:-translate-y-0.5"}`}
                      >
                        <div
                          className={`absolute inset-0 bg-gradient-to-r ${item.glow} transition-opacity duration-300 ${isActive ? "opacity-100" : "opacity-0"}`}
                        />
                        <div className="relative">
                          <p className="text-sm font-semibold text-gray-900 dark:text-light-100">
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-light-500 mt-1">
                            {item.workflowHint || item.name}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center gap-2 overflow-x-auto pb-3">
                  {TABS.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.key}
                        disabled={!tab.enabled}
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${tab.enabled ? "bg-primary-accent/10 text-primary-accent border border-primary-accent/30" : "bg-gray-100 dark:bg-dark-300 text-gray-500 dark:text-light-500 border border-gray-200 dark:border-dark-100 cursor-not-allowed"}`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                        {!tab.enabled && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase">
                            <Lock className="w-3 h-3" />
                            Soon
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 space-y-4">
                {activeWorkflow && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl border border-gray-200 dark:border-dark-100 bg-gradient-to-br from-white to-gray-50 dark:from-dark-300 dark:to-dark-300/80 shadow-sm p-4 sm:p-5 space-y-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-light-100 flex items-center gap-2">
                          <Clock3 className="w-4 h-4 text-primary-accent" />
                          AI Execution Timeline
                        </p>
                        <p className="text-xs text-gray-500 dark:text-light-500 mt-1">
                          The agent is building the UI plan step by step,
                          reusing the selected project context and components.
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border ${currentStatusMeta.dot.replace("bg-", "border-").replace("400", "200").replace("500", "200")} ${currentStatusMeta.tone} bg-white/70 dark:bg-dark-300/60`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${currentStatusMeta.dot} ${activeWorkflow.status === "thinking" || activeWorkflow.status === "planning" || activeWorkflow.status === "generating_ui" ? "animate-pulse" : ""}`}
                        />
                        {currentStatusMeta.label}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 overflow-x-auto pb-1">
                      {WORKFLOW_STAGES.map((stage, index) => {
                        const stageState = getTimelineStageState(
                          index,
                          timelineProgressIndex,
                          activeWorkflow,
                        );
                        const StageIcon = stage?.icon || MessageSquare;
                        return (
                          <div
                            key={stage.key}
                            className={`min-w-[220px] flex-1 rounded-xl border px-3 py-3 transition-all ${
                              stageState === "done"
                                ? "border-green-200 dark:border-green-900/40 bg-green-50/70 dark:bg-green-900/20"
                                : stageState === "active"
                                  ? "border-primary-accent/30 bg-primary-accent/10 dark:bg-primary-accent/15 shadow-md shadow-primary-accent/10"
                                  : stageState === "failed"
                                    ? "border-red-200 dark:border-red-900/40 bg-red-50/70 dark:bg-red-900/20"
                                    : "border-gray-200 dark:border-dark-100 bg-white/60 dark:bg-dark-300/40"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`w-8 h-8 rounded-lg flex items-center justify-center ${stageState === "done" ? "bg-green-500/10 text-green-600" : stageState === "active" ? "bg-primary-accent/10 text-primary-accent" : stageState === "failed" ? "bg-red-500/10 text-red-600" : "bg-gray-100 text-gray-500 dark:bg-dark-100 dark:text-light-400"}`}
                              >
                                {stageState === "done" ? (
                                  <CheckCircle2 className="w-4 h-4" />
                                ) : (
                                  <StageIcon
                                    className={`w-4 h-4 ${stageState === "active" ? "animate-pulse" : ""}`}
                                  />
                                )}
                              </span>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                                  {stage.label}
                                </p>
                                <p className="text-[11px] text-gray-500 dark:text-light-500 capitalize">
                                  {stageState}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="h-2 rounded-full bg-gray-200 dark:bg-dark-100 overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-primary-accent via-orange-500 to-amber-400"
                        initial={false}
                        animate={{
                          width: `${Math.max(14, ((timelineProgressIndex + 1) / WORKFLOW_STAGES.length) * 100)}%`,
                        }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600 dark:text-light-400">
                      <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                        <p className="font-medium text-gray-900 dark:text-light-100">
                          Current focus
                        </p>
                        <p className="mt-1">
                          {activeWorkflow.status === "completed"
                            ? "Plan finalized"
                            : activeWorkflow.status === "failed"
                              ? "Execution failed"
                              : currentStatusMeta.label}
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                        <p className="font-medium text-gray-900 dark:text-light-100">
                          Context memory
                        </p>
                        <p className="mt-1">
                          {selectedProject?.name || "No project selected yet"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                        <p className="font-medium text-gray-900 dark:text-light-100">
                          Session duration
                        </p>
                        <p className="mt-1">{workflowDuration}</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeWorkflow && activeMessages.length === 0 ? (
                  <div className="h-full min-h-[260px] flex items-center justify-center">
                    <div className="w-full max-w-lg text-center rounded-2xl border border-dashed border-gray-300 dark:border-dark-100 bg-gray-50/80 dark:bg-dark-300/30 p-8 sm:p-10">
                      <div className="w-14 h-14 mx-auto rounded-xl bg-primary-accent/10 flex items-center justify-center mb-4">
                        <Sparkles className="w-6 h-6 text-primary-accent" />
                      </div>
                      <p className="text-lg font-semibold text-gray-900 dark:text-light-100">
                        Start your AI workflow
                      </p>
                      <p className="text-sm text-gray-600 dark:text-light-500 mt-2">
                        Select a project card, then describe the screen you want
                        to generate.
                      </p>
                    </div>
                  </div>
                ) : (
                  <AnimatePresence initial={false}>
                    {activeMessages.map((entry) => {
                      const isUser = entry.role === "user";
                      const planContent =
                        entry.type === "plan" ? entry.content || {} : null;
                      const sectionCount = Array.isArray(planContent?.sections)
                        ? planContent.sections.length
                        : 0;
                      const selectedComponents = Array.from(
                        new Set(
                          (Array.isArray(planContent?.sections)
                            ? planContent.sections
                            : []
                          )
                            .map((section) =>
                              getComponentDisplayName(section.component),
                            )
                            .filter(Boolean),
                        ),
                      );
                      const executionSteps = Array.isArray(
                        planContent?.executionSteps,
                      )
                        ? planContent.executionSteps
                        : [];
                      return (
                        <motion.div
                          key={entry.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex items-start gap-3 ${isUser ? "justify-end" : "justify-start"}`}
                        >
                          {!isUser && (
                            <div className="w-8 h-8 rounded-full bg-primary-accent/10 text-primary-accent flex items-center justify-center shrink-0">
                              <MessageSquare className="w-4 h-4" />
                            </div>
                          )}

                          <div
                            className={`max-w-[92%] sm:max-w-[82%] rounded-2xl px-4 py-3 border shadow-sm ${isUser ? "bg-gradient-to-r from-primary-accent to-orange-600 border-primary-accent/20 text-white" : "bg-gray-50 dark:bg-dark-300/50 border-gray-200 dark:border-dark-100 text-gray-900 dark:text-light-100"}`}
                          >
                            {entry.type === "plan" ? (
                              <div className="space-y-4">
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-light-500">
                                      AI plan
                                    </p>
                                    <p className="text-sm font-semibold mt-1">
                                      {planContent.pageName ||
                                        entry.content?.pageName ||
                                        "Untitled UI"}
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-light-500 mt-1">
                                      {
                                        summarizePlan(
                                          planContent,
                                          selectedProject?.name,
                                        ).reason
                                      }
                                    </p>
                                  </div>
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-primary-accent/10 text-primary-accent border border-primary-accent/20">
                                    <Sparkles className="w-3 h-3" />
                                    Structured plan
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                  <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                                    <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                                      Layout
                                    </p>
                                    <p className="text-sm font-medium mt-1 capitalize">
                                      {planContent.layout || "vertical"}
                                    </p>
                                  </div>
                                  <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                                    <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                                      Sections
                                    </p>
                                    <p className="text-sm font-medium mt-1">
                                      {sectionCount}
                                    </p>
                                  </div>
                                  <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                                    <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                                      Components
                                    </p>
                                    <p className="text-sm font-medium mt-1">
                                      {selectedComponents.length}
                                    </p>
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-light-500">
                                    Sections preview
                                  </p>
                                  <div className="space-y-2">
                                    {(planContent.sections || []).map(
                                      (section, index) => (
                                        <div
                                          key={`${section.type || section.key || index}`}
                                          className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3 flex items-start justify-between gap-3"
                                        >
                                          <div>
                                            <p className="text-sm font-medium capitalize">
                                              {section.type}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-light-500 mt-1">
                                              Component:{" "}
                                              {getComponentDisplayName(
                                                section.component,
                                              ) || "Auto-selected"}
                                            </p>
                                          </div>
                                          <div className="text-right text-xs text-gray-500 dark:text-light-500">
                                            <p>
                                              Columns: {section.columns || 1}
                                            </p>
                                            <p>
                                              {Array.isArray(section.items)
                                                ? section.items.length
                                                : 0}{" "}
                                              items
                                            </p>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-light-500">
                                      Execution steps
                                    </p>
                                    <div className="mt-2 space-y-1.5">
                                      {executionSteps.map((step, index) => (
                                        <div
                                          key={`${step}-${index}`}
                                          className="flex items-center gap-2 text-sm text-gray-700 dark:text-light-300"
                                        >
                                          <span className="w-5 h-5 rounded-full bg-primary-accent/10 text-primary-accent flex items-center justify-center text-[11px] font-medium">
                                            {index + 1}
                                          </span>
                                          <span>{step}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-light-500">
                                      Components used
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {selectedComponents.length > 0 ? (
                                        selectedComponents.map((component) => (
                                          <span
                                            key={component}
                                            className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-dark-100 text-gray-700 dark:text-light-300 border border-gray-200 dark:border-dark-100"
                                          >
                                            {component}
                                          </span>
                                        ))
                                      ) : (
                                        <span className="text-sm text-gray-500 dark:text-light-500">
                                          No component mapping available
                                        </span>
                                      )}
                                    </div>
                                    {Array.isArray(planContent.notes) &&
                                      planContent.notes.length > 0 && (
                                        <div className="mt-3 rounded-lg border border-dashed border-primary-accent/30 bg-primary-accent/5 p-3">
                                          <p className="text-xs font-semibold uppercase tracking-wide text-primary-accent">
                                            Architect notes
                                          </p>
                                          <div className="mt-1 space-y-1">
                                            {planContent.notes.map(
                                              (note, index) => (
                                                <p
                                                  key={`${note}-${index}`}
                                                  className="text-sm text-gray-700 dark:text-light-300"
                                                >
                                                  {note}
                                                </p>
                                              ),
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            ) : entry.type === "success" ? (
                              <div className="space-y-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                                      <CheckCircle2 className="w-5 h-5" />
                                      <p className="font-semibold">
                                        {entry.content.title}
                                      </p>
                                    </div>
                                    <p className="text-sm mt-2">
                                      Generated screen:{" "}
                                      <span className="font-semibold">
                                        {entry.content.screenName}
                                      </span>
                                    </p>
                                  </div>
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-green-500/10 text-green-700 dark:text-green-300 border border-green-500/20">
                                    Completed
                                  </span>
                                </div>

                                {activePlan && (
                                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                    <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                                      <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                                        Page
                                      </p>
                                      <p className="text-sm font-medium mt-1">
                                        {activePlan.pageName ||
                                          entry.content.screenName}
                                      </p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                                      <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                                        Layout
                                      </p>
                                      <p className="text-sm font-medium mt-1 capitalize">
                                        {activePlan.layout || "vertical"}
                                      </p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                                      <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                                        Sections
                                      </p>
                                      <p className="text-sm font-medium mt-1">
                                        {Array.isArray(activePlan.sections)
                                          ? activePlan.sections.length
                                          : 0}
                                      </p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/50 p-3">
                                      <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                                        Duration
                                      </p>
                                      <p className="text-sm font-medium mt-1">
                                        {workflowDuration}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                <div className="flex flex-wrap gap-2">
                                  <button className="px-3 py-1.5 rounded-lg text-xs font-medium border border-primary-accent/35 text-primary-accent bg-primary-accent/10">
                                    Open in Figma
                                  </button>
                                  <button className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-dark-100 text-gray-700 dark:text-light-400 bg-white/70 dark:bg-dark-300/60">
                                    View generation logs
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                {entry.content}
                              </p>
                            )}
                            <p
                              className={`text-[11px] mt-2 ${isUser ? "text-white/80" : "text-gray-500 dark:text-light-500"}`}
                            >
                              {formatTime(entry.timestamp)}
                            </p>
                          </div>

                          {isUser && (
                            <div className="w-8 h-8 rounded-full bg-gray-900/10 dark:bg-light-100/10 text-gray-700 dark:text-light-200 flex items-center justify-center shrink-0">
                              <User className="w-4 h-4" />
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                )}

                {activeWorkflow?.status === "thinking" && (
                  <div className="rounded-xl border border-blue-200 dark:border-blue-800/40 bg-blue-50/70 dark:bg-blue-900/20 px-4 py-3 flex items-center gap-3">
                    <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-300 animate-spin" />
                    <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                      AI is analyzing the task...
                    </span>
                    <span className="flex items-center gap-1 ml-auto">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" />
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce [animation-delay:300ms]" />
                    </span>
                  </div>
                )}

                {activeWorkflow && activeLogs.length > 0 && (
                  <div className="rounded-xl border border-gray-200 dark:border-dark-100 bg-white/70 dark:bg-dark-300/55 p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <TerminalSquare className="w-4 h-4 text-primary-accent" />
                        <p className="text-sm font-semibold text-gray-900 dark:text-light-100">
                          Execution activity
                        </p>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-light-500">
                        Streaming-like updates
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200 dark:bg-dark-100 overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-primary-accent to-orange-600"
                        initial={false}
                        animate={{
                          width:
                            activeWorkflow.status === "completed"
                              ? "100%"
                              : activeWorkflow.status === "failed"
                                ? "100%"
                                : activeWorkflow.status === "generating_ui"
                                  ? "82%"
                                  : activeWorkflow.status === "planning"
                                    ? "56%"
                                    : activeWorkflow.status === "thinking"
                                      ? "34%"
                                      : "12%",
                        }}
                        transition={{ duration: 0.35 }}
                      />
                    </div>
                    <p className="text-sm text-gray-700 dark:text-light-300">
                      Current status:{" "}
                      <span className="font-medium">
                        {activeWorkflow.status}
                      </span>
                    </p>
                    <div className="rounded-lg border border-gray-200 dark:border-dark-100 bg-gray-50 dark:bg-dark-300/40 p-3 max-h-44 overflow-y-auto space-y-2">
                      {activeLogs.map((log, index) => {
                        const isRecent = index === activeLogs.length - 1;
                        return (
                          <div
                            key={`${log}-${index}`}
                            className="flex items-start gap-2 text-xs"
                          >
                            <span
                              className={`mt-1.5 w-2 h-2 rounded-full ${isRecent ? "bg-primary-accent animate-pulse" : "bg-gray-400 dark:bg-light-500"}`}
                            />
                            <p
                              className={`${isRecent ? "text-gray-900 dark:text-light-100 font-medium" : "text-gray-600 dark:text-light-500"}`}
                            >
                              {log}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {activeWorkflow?.status === "completed" && activePlan && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl border border-green-200 dark:border-green-900/40 bg-green-50/70 dark:bg-green-900/20 p-4 sm:p-5 shadow-sm space-y-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-green-800 dark:text-green-200 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Workflow summary
                        </p>
                        <p className="text-xs text-green-700/90 dark:text-green-300/80 mt-1">
                          The AI architect completed the UI plan and preserved
                          the selected project context.
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-green-500/10 text-green-700 dark:text-green-300 border border-green-500/20">
                        Completed
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                      <div className="rounded-xl border border-white/70 dark:border-dark-100 bg-white/80 dark:bg-dark-300/60 p-3">
                        <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                          Page name
                        </p>
                        <p className="text-sm font-semibold mt-1 text-gray-900 dark:text-light-100">
                          {activePlan.pageName || "Untitled UI"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-white/70 dark:border-dark-100 bg-white/80 dark:bg-dark-300/60 p-3">
                        <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                          Layout
                        </p>
                        <p className="text-sm font-semibold mt-1 capitalize text-gray-900 dark:text-light-100">
                          {activePlan.layout || "vertical"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-white/70 dark:border-dark-100 bg-white/80 dark:bg-dark-300/60 p-3">
                        <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                          Sections
                        </p>
                        <p className="text-sm font-semibold mt-1 text-gray-900 dark:text-light-100">
                          {Array.isArray(activePlan.sections)
                            ? activePlan.sections.length
                            : 0}
                        </p>
                      </div>
                      <div className="rounded-xl border border-white/70 dark:border-dark-100 bg-white/80 dark:bg-dark-300/60 p-3">
                        <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                          Components
                        </p>
                        <p className="text-sm font-semibold mt-1 text-gray-900 dark:text-light-100">
                          {planSummary.components.length || "Auto-selected"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-white/70 dark:border-dark-100 bg-white/80 dark:bg-dark-300/60 p-3">
                        <p className="text-[11px] uppercase text-gray-500 dark:text-light-500">
                          Duration
                        </p>
                        <p className="text-sm font-semibold mt-1 text-gray-900 dark:text-light-100">
                          {workflowDuration}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {(activePlan.notes || [])
                        .slice(0, 3)
                        .map((note, index) => (
                          <span
                            key={`${note}-${index}`}
                            className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/80 dark:bg-dark-300/70 text-gray-700 dark:text-light-300 border border-green-200 dark:border-green-900/30"
                          >
                            {note}
                          </span>
                        ))}
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="sticky bottom-0 border-t border-gray-200 dark:border-dark-100 bg-white/95 dark:bg-primary-card/95 backdrop-blur-sm px-4 sm:px-6 py-4">
                <div className="mb-3 flex items-center justify-between rounded-xl border border-gray-200 dark:border-dark-100 bg-gray-50 dark:bg-dark-300/40 px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${currentStatusMeta.dot} ${activeWorkflow?.status === "thinking" || activeWorkflow?.status === "planning" || activeWorkflow?.status === "generating_ui" ? "animate-pulse" : ""}`}
                    />
                    <StatusIcon
                      className={`w-4 h-4 ${currentStatusMeta.tone} ${activeWorkflow?.status === "thinking" ? "animate-spin" : ""}`}
                    />
                    <span
                      className={`text-sm font-medium ${currentStatusMeta.tone}`}
                    >
                      {currentStatusMeta.label}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-light-500">
                    {selectedProject
                      ? selectedProject.name
                      : "Select a project"}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={onComposerKeyDown}
                    placeholder={
                      selectedProject
                        ? `Describe the screen to generate for ${selectedProject.name}...`
                        : "Select a project to start..."
                    }
                    disabled={!selectedProject}
                    className="flex-1 min-h-[44px] max-h-[220px] resize-none bg-transparent text-sm outline-none p-2"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleSend}
                      disabled={!message.trim() || !selectedProject}
                      className="px-3 py-2"
                      icon={<Send className="w-4 h-4" />}
                    >
                      Send
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default AIStudio;
